// Travelpayouts Data API Service
// Docs: https://api.travelpayouts.com/documentation

const BASE_URL = 'https://api.travelpayouts.com';
const TOKEN = process.env.NEXT_PUBLIC_TRAVELPAYOUTS_TOKEN || '';

export interface TravelpayoutsFlightPrice {
  price: number;
  airline: string;
  flight_number: number;
  departure_at: string;
  return_at: string;
  expires_at: string;
  destination?: string;
  origin?: string;
  transfers?: number;
  duration?: number;
  link?: string;
}

export interface CheapFlightsResponse {
  success: boolean;
  data: Record<string, Record<string, TravelpayoutsFlightPrice>>;
  error?: string;
}

export interface CalendarFlightPrice {
  price: number;
  airline: string;
  flight_number: number;
  departure_at: string;
  return_at: string;
  expires_at: string;
}

export interface PopularDestination {
  destination: string;
  origin: string;
  price: number;
  transfers: number;
  airline: string;
  departure_at: string;
  return_at: string;
}

export const SUPPORTED_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
  { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
] as const;

export type CurrencyCode = typeof SUPPORTED_CURRENCIES[number]['code'];

export function getCurrencySymbol(code: string): string {
  const found = SUPPORTED_CURRENCIES.find((c) => c.code === code);
  return found ? found.symbol : code;
}

export function formatPrice(price: number, currencyCode: string): string {
  const symbol = getCurrencySymbol(currencyCode);
  if (currencyCode === 'JPY' || currencyCode === 'INR') {
    return `${symbol}${Math.round(price).toLocaleString()}`;
  }
  return `${symbol}${price.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/**
 * Fetch cheapest flights for a route
 * GET /v1/prices/cheap
 */
export async function fetchCheapFlights(params: {
  origin: string;
  destination?: string;
  depart_date?: string;
  return_date?: string;
  currency?: string;
  page?: number;
}): Promise<CheapFlightsResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('origin', params.origin);
  if (params.destination) searchParams.set('destination', params.destination);
  if (params.depart_date) searchParams.set('depart_date', params.depart_date);
  if (params.return_date) searchParams.set('return_date', params.return_date);
  if (params.currency) searchParams.set('currency', params.currency);
  if (params.page) searchParams.set('page', String(params.page));
  if (TOKEN) searchParams.set('token', TOKEN);

  const res = await fetch(
    `${BASE_URL}/v1/prices/cheap?${searchParams.toString()}`,
    {
      headers: TOKEN ? { 'x-access-token': TOKEN } : {},
      next: { revalidate: 3600 },
    }
  );

  if (!res.ok) {
    throw new Error(`Travelpayouts API error: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

/**
 * Fetch cheapest non-stop flights
 * GET /v1/prices/direct
 */
export async function fetchDirectFlights(params: {
  origin: string;
  destination?: string;
  depart_date?: string;
  return_date?: string;
  currency?: string;
}): Promise<CheapFlightsResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('origin', params.origin);
  if (params.destination) searchParams.set('destination', params.destination);
  if (params.depart_date) searchParams.set('depart_date', params.depart_date);
  if (params.return_date) searchParams.set('return_date', params.return_date);
  if (params.currency) searchParams.set('currency', params.currency);
  if (TOKEN) searchParams.set('token', TOKEN);

  const res = await fetch(
    `${BASE_URL}/v1/prices/direct?${searchParams.toString()}`,
    {
      headers: TOKEN ? { 'x-access-token': TOKEN } : {},
      next: { revalidate: 3600 },
    }
  );

  if (!res.ok) {
    throw new Error(`Travelpayouts API error: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

/**
 * Fetch popular destinations from an origin
 * GET /v2/prices/latest
 */
export async function fetchPopularDestinations(params: {
  origin: string;
  currency?: string;
  limit?: number;
}): Promise<{ success: boolean; data: PopularDestination[]; error?: string }> {
  const searchParams = new URLSearchParams();
  searchParams.set('origin', params.origin);
  if (params.currency) searchParams.set('currency', params.currency);
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (TOKEN) searchParams.set('token', TOKEN);

  const res = await fetch(
    `${BASE_URL}/v2/prices/latest?${searchParams.toString()}`,
    {
      headers: TOKEN ? { 'x-access-token': TOKEN } : {},
      next: { revalidate: 3600 },
    }
  );

  if (!res.ok) {
    throw new Error(`Travelpayouts API error: ${res.status} ${res.statusText}`);
  }

  return res.json();
}

/**
 * Build Travelpayouts affiliate search link for flights
 */
export function buildFlightSearchLink(params: {
  origin: string;
  destination: string;
  depart_date?: string;
  return_date?: string;
  adults?: number;
  currency?: string;
  marker?: string;
}): string {
  const base = 'https://www.aviasales.com/search';
  const dep = params.depart_date ? params.depart_date.replace(/-/g, '') : '';
  const ret = params.return_date ? params.return_date.replace(/-/g, '') : '';
  const adults = params.adults || 1;
  // Format: /ORIGIN_DEPART_DEST_RETURN_ADULTS
  return `${base}/${params.origin}${dep}${params.destination}${ret}${adults}`;
}
